import Vue from 'vue';
import Vuex from 'vuex';
import { createAuthModule } from '@shared/auth/store';
import { createBrandingModule } from '@shared/branding/store';
import { assessmentState, assessmentMutations, questionToApiPayload, apiQuestionToEditor } from '@shared/stores/assessment';
import { http } from '../api/http';
import { testsApi, questionsApi } from '../api/authoring';

Vue.use(Vuex);

// A11 (G07 §2.10, PE-10): el saldo de tokens del Tutor IA es AHORA server-side
// (GET /api/ai/tokens es la fuente de verdad). El store solo cachea el último
// saldo conocido para pintar la UI sin parpadeo; los saldos antiguos que vivían
// en localStorage se ignoran (§5.9 #22). `total` es solo un máximo visto para la
// barra de progreso del topbar; no es autoritativo.
const DEFAULT_TOKENS = { left: 5, total: 5, enabled: true, loaded: false };

const store = new Vuex.Store({
  modules: {
    auth: createAuthModule(http),
    branding: createBrandingModule(http),
  },
  state: {
    ...assessmentState(),
    ui: {
      ...assessmentState().ui,
      toast: { visible: false, message: '' },
      commandPaletteOpen: false,
      testRunner: { open: false, courseId: null },
      tokenPurchaseOpen: false,
    },
    tokens: { ...DEFAULT_TOKENS },
  },
  getters: {
    'ui/toast':              (s) => s.ui.toast,
    'ui/commandPaletteOpen': (s) => s.ui.commandPaletteOpen,
    'ui/testRunner':         (s) => s.ui.testRunner,
    'ui/tokenPurchaseOpen':  (s) => s.ui.tokenPurchaseOpen,
    'tokens/left':           (s) => s.tokens.left,
    'tokens/total':          (s) => s.tokens.total,
    'tokens/enabled':        (s) => s.tokens.enabled,
    'tokens/canSpend':       (s) => s.tokens.left > 0,
  },
  mutations: {
    ...assessmentMutations,
    SHOW_TOAST(state, message) { state.ui.toast = { visible: true, message }; },
    HIDE_TOAST(state)          { state.ui.toast.visible = false; },
    SET_PALETTE(state, open)   { state.ui.commandPaletteOpen = open; },
    OPEN_TEST_RUNNER(state, courseId) { state.ui.testRunner = { open: true, courseId }; },
    CLOSE_TEST_RUNNER(state)          { state.ui.testRunner = { open: false, courseId: null }; },
    OPEN_TOKEN_PURCHASE(state)  { state.ui.tokenPurchaseOpen = true; },
    CLOSE_TOKEN_PURCHASE(state) { state.ui.tokenPurchaseOpen = false; },
    // Aplica el saldo autoritativo devuelto por el servidor. `total` solo sube
    // (máximo visto) para que la barra de progreso del topbar tenga sentido.
    SET_TOKENS(state, payload = {}) {
      const left = Math.max(0, Number(payload.balance != null ? payload.balance : state.tokens.left) || 0);
      state.tokens = {
        left,
        total: Math.max(left, state.tokens.total || 0),
        enabled: payload.enabled != null ? !!payload.enabled : state.tokens.enabled,
        loaded: true,
      };
    },
  },
  actions: {
    toast({ commit }, message) {
      commit('SHOW_TOAST', message);
      setTimeout(() => commit('HIDE_TOAST'), 2400);
    },

    /**
     * Navega al RUNNER REAL (delivery-app SPA, ruta /test/:attemptId servida bajo
     * /apps/delivery/ en producción) para un attempt ya existente. Es la misma
     * mecánica que A7 introdujo en Library.vue (openRealRunner), extraída al store
     * para que TODOS los puntos de entrada (Library, My Tests, Dashboard) abran el
     * runner real en vez del overlay mock, sin duplicar la lógica.
     */
    openRealRunner(_ctx, attemptId) {
      const base = process.env.NODE_ENV === 'production' ? '/apps/delivery' : '';
      window.location.assign(`${base}/test/${attemptId}`);
    },

    /**
     * Reanuda un attempt REAL existente y abre el runner real. Si el attempt
     * estaba pausado se hace `POST /api/attempts/:id/resume` (best-effort) antes
     * de navegar. Degrada con gracia al overlay mock si el resume falla de forma
     * dura, para no dejar al alumno bloqueado.
     */
    async resumeAttempt({ dispatch, commit }, { attemptId, status } = {}) {
      if (attemptId == null) return;
      try {
        if (status === 'paused') {
          await http.post(`/api/attempts/${attemptId}/resume`);
        }
        dispatch('openRealRunner', attemptId);
      } catch (_e) {
        // Fallback: overlay mock para no bloquear al alumno.
        commit('OPEN_TEST_RUNNER', attemptId);
      }
    },

    /**
     * Inicia un intento REAL (o una demo real) contra el backend y abre el runner
     * real, replicando exactamente el flujo de A7 (Library.vue → confirmLaunch):
     *  - demo:  POST /api/attempts/demo { product_id }
     *  - real:  resuelve una key activa del producto y POST /api/attempts
     *           { product_key_id, mode: 'practice' }
     * Devuelve el attempt creado. En caso de error o si no hay key activa, lanza
     * (o devuelve null) para que la vista degrade con gracia; el manejo del
     * fallback al overlay queda en la vista, que conoce el contexto de UI.
     *
     * @returns {Promise<object|null>} attempt creado, o null si no hay key activa.
     */
    async launchAttempt({ dispatch }, { productId, isDemo = false } = {}) {
      if (isDemo) {
        const { data } = await http.post('/api/attempts/demo', { product_id: productId });
        dispatch('openRealRunner', data.id);
        return data;
      }

      const keyId = await dispatch('resolveActiveKeyId', productId);
      if (!keyId) return null;

      const { data } = await http.post('/api/attempts', {
        product_key_id: keyId,
        mode: 'practice',
      });
      dispatch('openRealRunner', data.id);
      return data;
    },

    /**
     * Resuelve el id de una key ACTIVA del alumno para un producto dado.
     * Reutiliza GET /api/keys (mismo criterio que Library.vue/A7). Devuelve null
     * si no hay ninguna, para que el llamante avise "registra una key primero".
     */
    async resolveActiveKeyId(_ctx, productId) {
      try {
        const { data } = await http.get('/api/keys');
        const rows = Array.isArray(data) ? data : (data?.data ?? []);
        const match = rows.find((k) =>
          k.status === 'active' &&
          (k.product_id === productId || k.selected_product_id === productId));
        return match?.id ?? null;
      } catch (_e) {
        return null;
      }
    },

    /**
     * A11: carga el saldo real de tokens del Tutor IA desde el backend, que es
     * la ÚNICA fuente de verdad. Degrada en silencio si el endpoint falla (p. ej.
     * permiso learn:UseAiTutor ausente): la UI se pinta con el último saldo
     * cacheado y el tutor quedará deshabilitado por `enabled=false` si procede.
     */
    async fetchTokens({ commit }) {
      try {
        const { data } = await http.get('/api/ai/tokens');
        commit('SET_TOKENS', data);
        return data;
      } catch (_e) {
        return null;
      }
    },

    /**
     * A11: consulta al Tutor IA. Consume 1 token REAL en el servidor y devuelve
     * la respuesta del modelo + el saldo restante. Lanza para que la vista
     * distinga NO_TOKENS (402) de AI_UNAVAILABLE (503) y degrade con gracia.
     *
     * @returns {Promise<{answer:string, tokens:object}>}
     */
    async askTutor({ commit }, { question, context } = {}) {
      const { data } = await http.post('/api/ai/tutor', { question, context });
      if (data && data.tokens) commit('SET_TOKENS', data.tokens);
      return data;
    },

    /**
     * Ajuste de saldo por seed/admin. El checkout de compra REAL queda fuera de
     * alcance (§5.9 #21); este atajo concede tokens sin cobro y refresca el saldo.
     */
    async grantTokens({ commit }, amount) {
      const { data } = await http.post('/api/ai/tokens/grant', { amount });
      commit('SET_TOKENS', data);
      return data;
    },

    /**
     * Persiste el assessment actual en el backend real.
     * - Crea/actualiza cada pregunta (upsert por backendId).
     * - Crea/actualiza el test y sincroniza su composición de preguntas.
     * Devuelve el test guardado.
     */
    async saveAssessmentToBackend({ state, commit }) {
      const a = state.assessment;

      // 1) Upsert de preguntas.
      const questionIds = [];
      for (const q of a.questions) {
        const payload = questionToApiPayload(q);
        let saved;
        if (q.backendId) {
          saved = await questionsApi.update(q.backendId, payload);
        } else {
          saved = await questionsApi.create(payload);
          commit('SET_QUESTION_BACKEND_ID', { id: q.id, backendId: saved.id });
        }
        questionIds.push(saved.id || q.backendId);
      }

      // 2) Upsert del test con la composición.
      const testPayload = {
        name: a.title || 'Untitled assessment',
        description: a.description || '',
        type: 'assessment',
        time_limit_min: a.timeLimitMin || null,
        question_count: a.questionCount || null,
        per_question_weighting: !!a.perQuestionWeighting,
        passing_score: a.passingScore != null ? a.passingScore : undefined,
        max_attempts: a.maxAttempts || null,
        question_ids: questionIds,
      };

      let test;
      if (a.backendId) {
        test = await testsApi.update(a.backendId, testPayload);
      } else {
        test = await testsApi.create(testPayload);
        commit('UPDATE_ASSESSMENT', { backendId: test.id });
      }

      return test;
    },

    /**
     * Carga un test existente del backend dentro del editor.
     * Mapea el test y sus preguntas al shape del editor y reemplaza el
     * assessment en curso. Devuelve el assessment cargado.
     */
    async loadAssessmentFromBackend({ commit }, testId) {
      const test = await testsApi.preview(testId);
      const questions = Array.isArray(test.questions)
        ? test.questions.map((row) => apiQuestionToEditor(row))
        : [];

      const assessment = {
        backendId: test.id,
        title: test.name || 'Untitled assessment',
        description: test.description || '',
        passingScore: test.passing_score != null ? Number(test.passing_score) : 70,
        maxAttempts: test.max_attempts || null,
        timeLimitMin: test.time_limit_min || null,
        questionCount: test.question_count || null,
        perQuestionWeighting: !!test.per_question_weighting,
        questions,
      };

      commit('LOAD_ASSESSMENT', assessment);
      return assessment;
    },
  },
});

export default store;
